import GIcon, { type IconSize } from '@consumer/components/GIcon.vue'
import type { OmniSearchResult } from '@consumer/types/serializers'
import ExpandedSectionInfiniteScroll from '@consumer/components/omni_search/ExpandedSectionInfiniteScroll.vue'
import { trackUserFlow, type UserFlow } from '@shared/tracking'
import { defaultIconFor } from './consumer_utils'

export { defaultIconFor } from '@consumer/modules/v2/layout/Header/OmniSearch/utils'

// Reveals a hidden element, animating its height.
export function reveal (el: HTMLElement) {
  el.style.overflow = 'hidden'
  el.classList.remove('hidden')

  const endHeight = `${el.offsetHeight}px`
  const startHeight = el.style.height = '0px'

  requestAnimationFrame(() => {
    const animation = el.animate({
      height: [startHeight, endHeight],
    }, { duration: 250, easing: 'ease-out' })
    animation.onfinish = () => {
      el.style.height = el.style.overflow = ''
    }
  })
}

export function setupSearchItemIcon (id: string, result: OmniSearchResult, size: IconSize = 'xl', className?: string) {
  const el = document.getElementById(id)
  if (!el) return console.warn(`No element found with id ${id}`)
  createApp(
    GIcon,
    {
      icon: defaultIconFor(result),
      class: className,
      size,
    },
  ).mount(el)
}

export function setupOmniSearchExpandedSectionInfiniteScroll (id: string) {
  const el = document.getElementById(id)
  if (!el) return console.warn(`No element found with id ${id}`)
  createApp(ExpandedSectionInfiniteScroll).mount(el)
}

// Checks yelp images for rendered places, and in case they failed to load,
// replaces them with a default image.
export function replaceBrokenPlaceImages (placeDefaultImageUrl: string) {
  document.querySelectorAll<HTMLImageElement>('.giftly-v3--business-image').forEach((img) => {
    img.addEventListener('error', () => { img.src = placeDefaultImageUrl })
    if (img.complete) img.src = img.src // eslint-disable-line no-self-assign
  })
}

// Scrolls the user to the results section when selecting a different page of results.
export function scrollCardsIntoView () {
  const urlParams = new URLSearchParams(location.search)
  const page = parseInt(urlParams.get('page') || '1')
  if (page > 1)
    document.querySelector('section:has(.gift-cards__pagination)')?.scrollIntoView({ behavior: 'smooth' })
}

// Collapse the section, and allow the user to manually expand it.
export function collapseLongSections () {
  document.querySelectorAll<HTMLElement>('[data-collapsible=\'toggle\']').forEach((el) => {
    const container = el.closest<HTMLElement>('[data-collapsible=\'container\']')
    if (!container) return console.warn('Missing collapsible container', el)

    const fullHeight = container.offsetHeight

    const maxHeight = Number(container.dataset.collapsibleHeight)
    if (!isNaN(maxHeight)) {
      if (fullHeight < maxHeight) return
      container.style.height = `${maxHeight}px`
    }
    else {
      const limitHeight = 500
      if (fullHeight < limitHeight) return
      container.style.height = `${Math.max(fullHeight / 3, limitHeight)}px`
    }

    el.classList.remove('hidden')
    el.addEventListener('click', () => {
      container.addEventListener('transitionend', () => container.style.height = 'auto')
      container.style.transition = 'height 0.3s ease'
      container.style.height = `${container.scrollHeight}px`
      el.classList.add('hidden')
    })
  })
}

export function setupDragToScroll (
  el: HTMLElement,
  leftButton: HTMLElement,
  rightButton: HTMLElement,
  scrollBy: number = 1000) {
  let isDown = false
  let startX: number
  let scrollLeft: number
  const ratio = 1 // Increase to scroll faster.
  leftButton = leftButton ?? el.querySelector('[name="Scroll Left"]')
  rightButton = rightButton ?? el.querySelector('[name="Scroll Right"]')

  leftButton?.addEventListener('click', () => {
    el.scrollBy({ left: -1 * scrollBy, behavior: 'smooth' })
  })

  rightButton?.addEventListener('click', () => {
    el.scrollBy({ left: scrollBy, behavior: 'smooth' })
  })

  el.addEventListener('mousedown', (e) => {
    isDown = true
    el.classList.add('active')
    startX = e.pageX - el.offsetLeft
    scrollLeft = el.scrollLeft
  })

  el.addEventListener('mouseleave', () => {
    isDown = false
    el.classList.remove('active')
  })

  el.addEventListener('mouseup', () => {
    isDown = false
    el.classList.remove('active')
  })

  el.addEventListener('mousemove', (e) => {
    if (!isDown) return
    e.preventDefault()
    const x = e.pageX - el.offsetLeft
    const walk = (x - startX) * ratio
    el.scrollLeft = scrollLeft - walk
  })
}

export function setupVanillaSwiper (carouselId: string, trackAs?: UserFlow) {
  const carousel = document.getElementById(carouselId)
  const swiperWrapper = carousel?.querySelector<HTMLElement>('.swiper')
  const swipeLeftButton = carousel?.querySelector<HTMLButtonElement>('.swiper-button-left')
  const swipeRightButton = carousel?.querySelector<HTMLButtonElement>('.swiper-button-right')
  const swiperCta = carousel?.querySelectorAll<HTMLElement>('.swiper-cta')
  const slides = carousel?.querySelectorAll<HTMLElement>('.swiper-slide:not(.search-carousel-cta-slide)')

  const hideShowButtons = () => {
    if (!swiperWrapper || !swipeLeftButton || !swipeRightButton) return

    if (swiperWrapper.scrollLeft > 0)
      swipeLeftButton.disabled = false
    else
      swipeLeftButton.disabled = true

    if (swiperWrapper.scrollLeft + swiperWrapper.clientWidth < swiperWrapper.scrollWidth)
      swipeRightButton.disabled = false
    else
      swipeRightButton.disabled = true

    if (trackAs)
      trackUserFlow(trackAs, 'scroll')
  }

  swiperWrapper?.addEventListener('scroll', hideShowButtons)

  const swipeDelta = 450

  swipeLeftButton?.addEventListener('click', () => {
    if (trackAs) trackUserFlow(trackAs, 'navigate_left')
    swiperWrapper?.scrollBy({ left: -swipeDelta, behavior: 'smooth' })
  })

  swipeRightButton?.addEventListener('click', () => {
    if (trackAs) trackUserFlow(trackAs, 'navigate_right')
    swiperWrapper?.scrollBy({ left: swipeDelta, behavior: 'smooth' })
  })

  swiperCta?.forEach((cta) => {
    cta.addEventListener('click', () => {
      if (trackAs) trackUserFlow(trackAs, 'click_cta')
    })
  })

  slides?.forEach((slide) => {
    slide.addEventListener('click', () => {
      if (trackAs) trackUserFlow(trackAs, 'click_slide')
    })
  })

  window.addEventListener('load', () => {
    hideShowButtons()
    if (swipeLeftButton?.disabled && swipeRightButton?.disabled) {
      swipeLeftButton.style.display = 'none'
      swipeRightButton.style.display = 'none'
    }
  })
}
